import FlipIcon from '@mui/icons-material/Flip';
import Icon from 'common/components/Elements/Icon/Icon';
import { clientUrlPath } from 'common/utilities/browser/url/url';
import { openNewWindowAction } from 'core/redux/actions/ui/uiActions';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import I18n from 'i18n/en-US';
import type React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useDispatch,useSelector } from 'react-redux';
import type { SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleResidueHover } from 'sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable';
import { addIdsToSelection,removeIdsFromSelection } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import { selectAlignedSequenceRangeIds } from 'sequenceAnalyser/utilities/selection/selection';

const DRAGGABLE_ITEM = 'SEQUENCE_LINE';

interface DraggableItem {
  type: typeof DRAGGABLE_ITEM;
  rowId: number;
}

interface ComponentProps {
  rowId: number;
  name: string;
  sequenceId: number;
  onDragNDrop?: (from: number, to: number) => void;
  alignedSequences: SequenceAlign[];
  url?: string;
  onResidueHover: HandleResidueHover;
  oppositeStrand?: boolean;
}

function SequenceTableRowTitle({
  rowId, sequenceId, name, onDragNDrop, alignedSequences, url, onResidueHover, oppositeStrand,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const selection = useSelector((state: ApplicationState) => state.sequenceAnalyser.selection);
  const selected = selection
    && selection.positionStart === undefined
    && selection.positionEnd === undefined
    && selection.ids.includes(sequenceId);

  const toggleSelection = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    let selectedIds = [sequenceId];

    if (e.shiftKey && selection.lastId) {
      selectedIds = selectAlignedSequenceRangeIds(alignedSequences, selection.lastId, sequenceId);
    }

    if (selected) {
      dispatch(removeIdsFromSelection(selectedIds));
    } else {
      dispatch(addIdsToSelection(selectedIds));
    }
  };

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAGGABLE_ITEM,
    item: { type: DRAGGABLE_ITEM, rowId } as DraggableItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isDraggingOver }, drop] = useDrop({
    accept: DRAGGABLE_ITEM,
    drop: (item: DraggableItem) => {
      if (onDragNDrop) onDragNDrop(item.rowId, rowId < item.rowId ? rowId : rowId - 1);
    },
    collect: (monitor) => ({
      isDraggingOver: monitor.isOver(),
    }),
  });

  return (
    <div
      role="button"
      className={[
        'title zoomable', // styled in SequencesStyling.style
        selected ? 'selectedRow' : '',
        isDraggingOver ? 'draggingOver' : '',
      ].join(' ')}
      ref={onDragNDrop ? drop : undefined}
      onClick={(e) => toggleSelection(e)}
      tabIndex={rowId}
      onKeyUp={() => {}}
      onDoubleClick={() => { if (url) dispatch(openNewWindowAction(clientUrlPath(url))); }}
      onMouseEnter={() => onResidueHover(undefined)}
    >
      <span
        className="name" // styled in SequencesStyling.style
        ref={onDragNDrop ? dragRef : undefined}
        style={{ opacity: isDragging ? 0.5 : 1 }}
      >
        {name.length === 0 ? (
          <span style={{ visibility: 'hidden' }}>No label</span>
        ) : (
          <span>{name}</span>
        )}
        {oppositeStrand && (
          <span title={I18n.SequenceAnalyser.SequenceTableRowTitle.oppositeStrand}>
            <Icon element={FlipIcon} ml rotate={-90} />
          </span>
        )}
      </span>

    </div>
  );
}

export default SequenceTableRowTitle;
